import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, Utensils, Edit, Trash2, GripVertical, ImageIcon, Upload,
  ChevronDown, ChevronUp, Tag, Check
} from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MenuManager = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", sort_order: 0, category_type: "food" });
  const [itemForm, setItemForm] = useState({
    category_id: "",
    name: "",
    description: "",
    price: "",
    image_url: "",
    image_data: "",
    variants: [],
    extras: [],
    is_available: true,
    prep_time: 15,
  });
  const [newVariant, setNewVariant] = useState({ name: "", price_modifier: 0 });
  const [newExtra, setNewExtra] = useState({ name: "", price: 0, is_required: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, itemRes] = await Promise.all([
        axios.get(`${API}/restaurant/categories`),
        axios.get(`${API}/restaurant/items`),
      ]);
      setCategories(catRes.data);
      setItems(itemRes.data);
      const expanded = {};
      catRes.data.forEach((cat) => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error("Failed to fetch menu data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/restaurant/categories/${editingCategory.id}`, categoryForm);
        toast.success("Category updated!");
      } else {
        await axios.post(`${API}/restaurant/categories`, categoryForm);
        toast.success("Category created!");
      }
      fetchData();
      setCategoryDialog(false);
      setCategoryForm({ name: "", description: "", sort_order: 0, category_type: "food" });
      setEditingCategory(null);
    } catch (error) {
      toast.error("Failed to save category");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    try {
      await axios.delete(`${API}/restaurant/categories/${id}`);
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  // Image Upload Handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Max 2MB allowed.");
      return;
    }

    setUploadingImage(true);
    
    // Convert to base64 for preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setItemForm({ ...itemForm, image_data: reader.result, image_url: "" });
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  // If editing existing item, upload to server
  const uploadImageToServer = async (itemId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/restaurant/items/${itemId}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded!");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload image");
    }
  };

  // Item CRUD
  const handleSaveItem = async () => {
    const payload = {
      ...itemForm,
      price: parseFloat(itemForm.price) || 0,
    };
    try {
      if (editingItem) {
        await axios.put(`${API}/restaurant/items/${editingItem.id}`, payload);
        toast.success("Item updated!");
      } else {
        await axios.post(`${API}/restaurant/items`, payload);
        toast.success("Item created!");
      }
      fetchData();
      setItemDialog(false);
      resetItemForm();
    } catch (error) {
      toast.error("Failed to save item");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    try {
      await axios.delete(`${API}/restaurant/items/${id}`);
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleToggleAvailable = async (item) => {
    try {
      await axios.put(`${API}/restaurant/items/${item.id}`, { is_available: !item.is_available });
      fetchData();
    } catch (error) {
      toast.error("Failed to update availability");
    }
  };

  const resetItemForm = () => {
    setItemForm({
      category_id: "",
      name: "",
      description: "",
      price: "",
      image_url: "",
      image_data: "",
      variants: [],
      extras: [],
      is_available: true,
    });
    setEditingItem(null);
  };

  const addVariant = () => {
    if (!newVariant.name.trim()) return;
    setItemForm({
      ...itemForm,
      variants: [...itemForm.variants, { ...newVariant, price_modifier: parseFloat(newVariant.price_modifier) || 0 }],
    });
    setNewVariant({ name: "", price_modifier: 0 });
  };

  const removeVariant = (index) => {
    setItemForm({
      ...itemForm,
      variants: itemForm.variants.filter((_, i) => i !== index),
    });
  };

  const addExtra = () => {
    if (!newExtra.name.trim()) return;
    setItemForm({
      ...itemForm,
      extras: [...itemForm.extras, { ...newExtra, price: parseFloat(newExtra.price) || 0 }],
    });
    setNewExtra({ name: "", price: 0, is_required: false });
  };

  const removeExtra = (index) => {
    setItemForm({
      ...itemForm,
      extras: itemForm.extras.filter((_, i) => i !== index),
    });
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || "", sort_order: cat.sort_order, category_type: cat.category_type || "food" });
    setCategoryDialog(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      name: item.name,
      description: item.description || "",
      price: item.price.toString(),
      image_url: item.image_url || "",
      image_data: item.image_data || "",
      variants: item.variants || [],
      extras: item.extras || [],
      is_available: item.is_available,
      prep_time: item.prep_time || 15,
    });
    setItemDialog(true);
  };

  const openNewItem = (categoryId) => {
    resetItemForm();
    setItemForm((prev) => ({ ...prev, category_id: categoryId }));
    setItemDialog(true);
  };

  const toggleCategory = (id) => {
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getItemsByCategory = (categoryId) => items.filter((i) => i.category_id === categoryId);
  
  const getItemImage = (item) => item.image_data || item.image_url;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="menu-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Utensils className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Menu Management</h1>
              <p className="text-sm text-muted-foreground">
                {categories.length} categories, {items.length} items
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Dialog open={categoryDialog} onOpenChange={(open) => { 
              setCategoryDialog(open); 
              if (!open) { setEditingCategory(null); setCategoryForm({ name: "", description: "", sort_order: 0, category_type: "food" }); }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? "Edit Category" : "New Category"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category Name *</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="e.g., Main Courses"
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={categoryForm.sort_order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kitchen Type *</Label>
                    <Select value={categoryForm.category_type} onValueChange={(v) => setCategoryForm({ ...categoryForm, category_type: v })}>
                      <SelectTrigger data-testid="category-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="food">Food Kitchen</SelectItem>
                        <SelectItem value="drink">Drinks Kitchen</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Items in this category will be routed to the selected kitchen dashboard</p>
                  </div>
                  <Button onClick={handleSaveCategory} className="w-full" data-testid="save-category-btn">
                    {editingCategory ? "Update Category" : "Create Category"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Button onClick={() => { resetItemForm(); setItemDialog(true); }} data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Menu Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Utensils className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-1">No menu items yet</h3>
              <p className="text-muted-foreground text-sm mb-4">Start by creating a category</p>
              <Button onClick={() => setCategoryDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <Card key={category.id} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                        {category.description && (
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                        )}
                      </div>
                      <Badge variant="outline">{getItemsByCategory(category.id).length} items</Badge>
                      <Badge className={category.category_type === "drink" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}>
                        {category.category_type === "drink" ? "Drinks" : "Food"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openEditCategory(category); }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                      {expandedCategories[category.id] ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {expandedCategories[category.id] && (
                  <CardContent className="p-4">
                    <div className="grid gap-3">
                      {getItemsByCategory(category.id).map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                          data-testid={`menu-item-${item.id}`}
                        >
                          {getItemImage(item) ? (
                            <img 
                              src={getItemImage(item)} 
                              alt={item.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                              <ImageIcon className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium truncate">{item.name}</h4>
                              {!item.is_available && (
                                <Badge variant="secondary" className="text-xs">Unavailable</Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-muted-foreground truncate">{item.description}</p>
                            )}
                            <p className="font-semibold text-primary">{formatCurrency(item.price)}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.variants?.length > 0 && item.variants.map((v) => (
                                <Badge key={v.id || v.name} variant="outline" className="text-xs">
                                  {v.name} {v.price_modifier > 0 && `+${formatCurrency(v.price_modifier)}`}
                                </Badge>
                              ))}
                              {item.extras?.length > 0 && item.extras.map((e) => (
                                <Badge key={`e-${e.id || e.name}`} variant="secondary" className="text-xs bg-green-100 text-green-700">
                                  +{e.name} {formatCurrency(e.price)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={item.is_available}
                              onCheckedChange={() => handleToggleAvailable(item)}
                            />
                            <Button variant="ghost" size="sm" onClick={() => openEditItem(item)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => openNewItem(category.id)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add item to {category.name}
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Item Dialog */}
        <Dialog open={itemDialog} onOpenChange={(open) => { setItemDialog(open); if (!open) resetItemForm(); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Menu Item" : "New Menu Item"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Item Image</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed">
                    {(itemForm.image_data || itemForm.image_url) ? (
                      <img 
                        src={itemForm.image_data || itemForm.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      data-testid="upload-image-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? "Uploading..." : "Upload Image"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      PNG, JPG up to 2MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={itemForm.category_id}
                  onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}
                >
                  <SelectTrigger data-testid="item-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g., Grilled Chicken"
                  data-testid="item-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Describe your dish..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>Price (USD) *</Label>
                <Input
                  type="number"
                  value={itemForm.price}
                  onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                  placeholder="0.00"
                  data-testid="item-price-input"
                />
              </div>
              
              {/* Variants (Single Choice) */}
              <div className="space-y-2">
                <Label>Variants (Single Choice - Size, etc.)</Label>
                <div className="space-y-2">
                  {itemForm.variants.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Tag className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1">{v.name}</span>
                      <span className="text-muted-foreground">+{formatCurrency(v.price_modifier)}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeVariant(i)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Large"
                      value={newVariant.name}
                      onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="+$"
                      value={newVariant.price_modifier}
                      onChange={(e) => setNewVariant({ ...newVariant, price_modifier: e.target.value })}
                      className="w-20"
                    />
                    <Button variant="outline" onClick={addVariant}>Add</Button>
                  </div>
                </div>
              </div>

              {/* Extras (Multi-Choice Add-ons) */}
              <div className="space-y-2">
                <Label>Extras / Add-ons (Multi-Choice)</Label>
                <p className="text-xs text-muted-foreground">Customers can select multiple extras</p>
                <div className="space-y-2">
                  {itemForm.extras.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="flex-1">{e.name}</span>
                      <span className="text-green-700">+{formatCurrency(e.price)}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeExtra(i)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Extra Cheese"
                      value={newExtra.name}
                      onChange={(e) => setNewExtra({ ...newExtra, name: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="+$"
                      value={newExtra.price}
                      onChange={(e) => setNewExtra({ ...newExtra, price: e.target.value })}
                      className="w-20"
                    />
                    <Button variant="outline" onClick={addExtra} className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100">
                      Add Extra
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Label>Available for ordering</Label>
                <Switch
                  checked={itemForm.is_available}
                  onCheckedChange={(checked) => setItemForm({ ...itemForm, is_available: checked })}
                />
              </div>
              <div className="space-y-2">
                <Label>Prep Time (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={itemForm.prep_time}
                  onChange={(e) => setItemForm({ ...itemForm, prep_time: parseInt(e.target.value) || 15 })}
                  placeholder="15"
                  data-testid="item-prep-time"
                />
                <p className="text-xs text-muted-foreground">Kitchen will see overdue alert if item takes longer</p>
              </div>
              
              <Button onClick={handleSaveItem} className="w-full" data-testid="save-item-btn">
                {editingItem ? "Update Item" : "Create Item"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default MenuManager;
